/**
 * Route metadata: the single source of truth for URLs, page titles and meta descriptions.
 * Imported by the browser router and by the Node build (HTML shells, sitemap), so it must stay free of
 * DOM and page-rendering code. Page renderers are registered in features/router/page-registry.ts.
 */
export type PageLanguage = 'en' | 'fr-CA';

export interface RouteMeta {
  readonly path: string;
  /** Analytics / lead-source name for the page. */
  readonly pageName: string;
  readonly title: string;
  readonly description: string;
  readonly lang?: PageLanguage;
}

export const ROUTES = [
  {
    path: '/',
    pageName: 'home',
    title: 'ReTax360: AI + Expertise. Built around how you work.',
    description:
      'Enterprise AI orchestration and workflow platform purpose-built for property tax operations. One foundation for clients, communication, data and documents.',
  },
  {
    path: '/platform',
    pageName: 'platform',
    title: 'Platform: Intelligence & Orchestration | ReTax360',
    description:
      'Smart routing between AI models, agents, business rules, APIs, systems and human experts, with confidence-based escalation and a shared foundation.',
  },
  {
    path: '/solutions',
    pageName: 'solutions',
    title: 'Solutions for Advisory Firms, Tax Practices & CRE Portfolios | ReTax360',
    description:
      'ReTax360 is configured around how advisory and valuation firms, property tax practices and enterprise CRE portfolios actually operate.',
  },
  {
    path: '/customers',
    pageName: 'customers',
    title: 'Customers: Real Workflows, Measurable Impact | ReTax360',
    description:
      'Built with property tax professionals, around the work they actually do. Observed, projected and target operational impact, clearly labeled.',
  },
  {
    path: '/security',
    pageName: 'security',
    title: 'Security & Trust | ReTax360',
    description:
      'Enterprise-grade by design. AI you can explain. Access controls, data ownership, AI governance and compliance information for IT and security reviewers.',
  },
  {
    path: '/insights',
    pageName: 'insights',
    title: 'Insights | ReTax360',
    description: 'Perspectives on AI orchestration, property tax operations and enterprise AI from ReTax360.',
  },
  {
    path: '/company',
    pageName: 'company',
    title: 'Our Thesis | ReTax360',
    description:
      'Built from inside the problem. Why ReTax360 exists and how we configure technology around the way property tax organizations work.',
  },
  {
    path: '/careers',
    pageName: 'careers',
    title: 'Careers | ReTax360',
    description:
      'ReTax360 is hiring senior, full-stack, frontend, backend and AI engineers, plus marketing and go-to-market roles. Build the future of intelligent work.',
  },
  {
    path: '/contact',
    pageName: 'contact',
    title: 'Contact | ReTax360',
    description:
      'Enterprise sales: sales@retax360.com. General inquiries and careers: info@retax360.com. ReTax360 LLC, Marina Del Rey, CA.',
  },
  {
    path: '/demo',
    pageName: 'demo',
    title: 'Book an Enterprise Demo | ReTax360',
    description:
      "See how ReTax360 can be configured around your organization's workflows, offices, markets and approval requirements.",
  },
  {
    path: '/calculator',
    pageName: 'calculator',
    title: 'Capacity Calculator | ReTax360',
    description:
      'Estimate how much professional capacity your property tax team could get back each year, using your own numbers.',
  },
  {
    path: '/quebec',
    pageName: 'quebec',
    title: 'ReTax360 au Québec | L’IA + l’expertise en fiscalité foncière',
    description:
      'ReTax360 : plateforme d’orchestration de l’IA et de gestion des flux de travail pour les opérations de fiscalité foncière au Québec et au Canada.',
    lang: 'fr-CA',
  },
  {
    path: '/privacy',
    pageName: 'privacy',
    title: 'Privacy Policy | ReTax360',
    description:
      'How ReTax360 LLC collects, uses, shares and protects Personal Data, and the privacy rights available to you.',
  },
  {
    path: '/terms',
    pageName: 'terms',
    title: 'Terms of Service | ReTax360',
    description: 'The Terms of Service governing use of ReTax360 LLC websites, forms and AI-assisted features.',
  },
] as const satisfies readonly RouteMeta[];

export type Route = (typeof ROUTES)[number];

export type RoutePath = Route['path'];

const ROUTES_BY_PATH: ReadonlyMap<string, Route> = new Map(ROUTES.map((route) => [route.path, route]));

export const findRoute = (path: string): Route | undefined => ROUTES_BY_PATH.get(path);

export const NOT_FOUND_META: RouteMeta = {
  path: '/404',
  pageName: '404',
  title: 'Page not found | ReTax360',
  description: 'This page does not exist.',
};

/** Legacy URLs. Must match the redirects in firebase.json and nginx.conf (enforced by a unit test). */
export const REDIRECTS: Readonly<Partial<Record<string, RoutePath>>> = {
  '/pricing': '/demo',
  '/about': '/company',
  '/about-us': '/company',
  '/team': '/company',
  '/contact-us': '/contact',
};

export const INSIGHT_ARTICLE_PREFIX = '/insights/';
